import { Control, Controller } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PaymentProvider } from "@/components/Checkout/Steps/Checkout/PaymentProvider/PaymentProvider";
import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { SubscriptionInfoBox } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import {
  paymentProviderIcons,
  paymentProviderLabels,
} from "@/components/Portal/Documents/PaymentMethods/PaymentProviderPicker/PaymentProviderPicker";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { DrCreditCardCardType } from "@/services/model";

export interface CheckoutPaymentCardProps {
  isInvoice: boolean;
  isCreditCard: boolean;
  cardType?: DrCreditCardCardType;
  maskedCardNumberString: string;
  /** Drives the provider picker shown when no payment mean is saved. */
  control: Control<{ paymentProvider: DrCreditCardCardType }>;
  /** When true, renders the "No charge" UX instead of requiring a payment method selection. */
  sumIsZero?: boolean;
  dataTestid?: string;
  /** The `portal:` translation key prefix - the roaming and Watch SIM checkouts each have their own copy. */
  keyPrefix?:
    | "cockpit.managementTile.roaming.checkout.paymentMethod"
    | "cockpit.managementTile.watchSim.checkout.paymentMethod";
}

/**
 * The "Zahlungsmittel" card of the redesigned checkouts: the saved payment
 * mean when one exists, the provider picker otherwise, and the hint that
 * changing it goes through the account settings.
 */
export const CheckoutPaymentCard = ({
  isInvoice,
  isCreditCard,
  cardType,
  maskedCardNumberString,
  control,
  sumIsZero = false,
  dataTestid = "checkout-payment-card",
  keyPrefix = "cockpit.managementTile.roaming.checkout.paymentMethod",
}: CheckoutPaymentCardProps) => {
  const { t } = useTranslation("portal", { keyPrefix });
  const { t: tDefault } = useTranslation();
  const navigate = useNavigate();

  return (
    <ProfileCard
      dataTestid={dataTestid}
      variant="white"
      title={t("title")}
      subtitle={isInvoice || isCreditCard ? t("subtitle") : undefined}
    >
      {isInvoice || isCreditCard ? (
        <SubscriptionInfoBox className="flex items-center gap-3">
          {isCreditCard && cardType != null && (
            <span className="shrink-0">{paymentProviderIcons[cardType]}</span>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p className="truncate-safe text-body-sm font-semibold leading-none text-foreground-primary">
              {isInvoice
                ? tDefault("portal:user-account.paymentMethods.invoice")
                : cardType != null && paymentProviderLabels[cardType]}
            </p>
            {isCreditCard && (
              <p className="truncate-safe text-body-sm leading-none text-foreground-primary">
                {maskedCardNumberString}
              </p>
            )}
          </div>
        </SubscriptionInfoBox>
      ) : (
        <Controller
          control={control}
          name="paymentProvider"
          render={({ field: { onChange, value } }) => (
            <PaymentProvider
              paymentProvider={value}
              sumIsZero={sumIsZero}
              onChange={onChange}
            />
          )}
        />
      )}

      {!isInvoice && (
        <Notification
          dataTestid={`${dataTestid}-hint`}
          description={
            <Trans
              t={t}
              i18nKey="hint"
              components={[
                <Link
                  key="settings-link"
                  type="button"
                  size="sm"
                  onClick={() => navigate(portalRoutes.documents.payments)}
                />,
              ]}
            />
          }
        />
      )}
    </ProfileCard>
  );
};
